import React, {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';

const ChatPanel = lazy(() => import('./ChatPanel'));

const MOBILE_BREAKPOINT = 768;
const HOVER_OPEN_MS = 850;
const DESKTOP_HOVER_MQ =
  '(hover: hover) and (pointer: fine) and (min-width: 769px)';
const ATTENTION_PULSE_KEY = 'wesam-chatbot-pulse-shown';
const HEADER_SLOT_ID = 'chat-nav-slot';

const STARTER_PROMPTS = [
  'Explain this page',
  'Find pricing',
  'Help me get started',
  'Compare plans',
  'Contact support',
];

function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function canHoverExpandChat() {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia(DESKTOP_HOVER_MQ).matches &&
    !prefersReducedMotion()
  );
}

function getHeaderSlot() {
  if (typeof document === 'undefined') return null;
  return document.getElementById(HEADER_SLOT_ID);
}

export default function Chatbot() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [hasLoadedPanel, setHasLoadedPanel] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT
  );
  const [showPulse, setShowPulse] = useState(false);
  const [messages, setMessages] = useState([]);
  const [model, setModel] = useState('gpt-4o');
  const [expanded, setExpanded] = useState(false);
  const [headerSlot, setHeaderSlot] = useState(null);

  const launcherRef = useRef(null);
  const panelRef = useRef(null);
  const previousFocusRef = useRef(null);
  const openRef = useRef(open);
  const hoverOpenTimerRef = useRef(null);

  openRef.current = open;

  const clearHoverOpenTimer = useCallback(() => {
    if (hoverOpenTimerRef.current) {
      clearTimeout(hoverOpenTimerRef.current);
      hoverOpenTimerRef.current = null;
    }
  }, []);

  const openChat = useCallback(() => {
    clearHoverOpenTimer();
    previousFocusRef.current = document.activeElement;
    try {
      sessionStorage.setItem('wesam-chatbot-opened', '1');
    } catch (_) {
      /* ignore */
    }
    setHasLoadedPanel(true);
    setOpen(true);
  }, [clearHoverOpenTimer]);

  const closeChat = useCallback(() => {
    clearHoverOpenTimer();
    setExpanded(false);
    setOpen(false);
    requestAnimationFrame(() => {
      if (launcherRef.current) {
        launcherRef.current.focus();
      } else if (
        previousFocusRef.current &&
        typeof previousFocusRef.current.focus === 'function'
      ) {
        previousFocusRef.current.focus();
      }
    });
  }, [clearHoverOpenTimer]);

  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  const toggleChat = useCallback(() => {
    if (openRef.current) closeChat();
    else openChat();
  }, [closeChat, openChat]);

  useEffect(() => {
    setHeaderSlot(getHeaderSlot());
  }, []);

  useEffect(() => {
    return () => clearHoverOpenTimer();
  }, [clearHoverOpenTimer]);

  const onLauncherMouseEnter = () => {
    if (!canHoverExpandChat()) return;
    if (openRef.current) return;
    clearHoverOpenTimer();
    hoverOpenTimerRef.current = setTimeout(() => {
      hoverOpenTimerRef.current = null;
      if (openRef.current) return;
      openChat();
    }, HOVER_OPEN_MS);
  };

  const onLauncherMouseLeave = () => {
    clearHoverOpenTimer();
  };

  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    try {
      if (sessionStorage.getItem(ATTENTION_PULSE_KEY) === '1') return undefined;
    } catch (_) {
      /* ignore */
    }
    if (prefersReducedMotion()) {
      try {
        sessionStorage.setItem(ATTENTION_PULSE_KEY, '1');
      } catch (_) {
        /* ignore */
      }
      return undefined;
    }
    const timer = setTimeout(() => {
      setShowPulse(true);
      try {
        sessionStorage.setItem(ATTENTION_PULSE_KEY, '1');
      } catch (_) {
        /* ignore */
      }
      setTimeout(() => setShowPulse(false), 1600);
    }, 1800);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (expanded && !isMobile) {
          setExpanded(false);
          return;
        }
        closeChat();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, closeChat, expanded, isMobile]);

  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    if (isMobile || expanded) {
      document.body.style.overflow = 'hidden';
    }
    const id = requestAnimationFrame(() => {
      const root = panelRef.current;
      if (!root) return;
      const focusable = root.querySelector(
        'button, [href], textarea, select, input, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable) focusable.focus();
    });
    return () => {
      cancelAnimationFrame(id);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, hasLoadedPanel, isMobile, expanded]);

  const launcher = (
    <div
      className="chat-launcher-wrap chat-launcher-wrap--header"
      onMouseEnter={onLauncherMouseEnter}
      onMouseLeave={onLauncherMouseLeave}
    >
      <button
        ref={launcherRef}
        type="button"
        className={`chat-launcher${showPulse ? ' chat-launcher--pulse' : ''}${
          open ? ' is-open' : ''
        }`}
        aria-label={open ? 'Close AI chat assistant' : 'Open AI chat assistant'}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={toggleChat}
      >
        <span className="chat-launcher__icon" aria-hidden="true">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path
              d="M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v7A2.5 2.5 0 0 1 17.5 16H9l-3.8 3.2c-.5.4-1.2.1-1.2-.5V6.5Z"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinejoin="round"
            />
            <circle cx="9" cy="10" r="1" fill="currentColor" />
            <circle cx="12" cy="10" r="1" fill="currentColor" />
            <circle cx="15" cy="10" r="1" fill="currentColor" />
          </svg>
        </span>
        <span className="chat-launcher__badge" aria-hidden="true">
          AI
        </span>
      </button>
    </div>
  );

  return (
    <>
      {headerSlot ? createPortal(launcher, headerSlot) : null}

      {open && hasLoadedPanel ? (
        <div
          ref={panelRef}
          className={`chat-panel-shell chat-panel-shell--header${
            isMobile ? ' chat-panel-shell--mobile' : ''
          }${expanded && !isMobile ? ' chat-panel-shell--expanded' : ''}`}
          role="dialog"
          aria-modal="true"
          aria-label="AI chat assistant"
          aria-expanded={expanded || isMobile}
        >
          <Suspense
            fallback={
              <div className="chat-panel-loading" role="status">
                Loading chat…
              </div>
            }
          >
            <ChatPanel
              messages={messages}
              setMessages={setMessages}
              model={model}
              setModel={setModel}
              starterPrompts={STARTER_PROMPTS}
              onClose={closeChat}
              isMobile={isMobile}
              pagePath={location.pathname}
              expanded={expanded}
              onToggleExpand={!isMobile ? toggleExpanded : undefined}
            />
          </Suspense>
        </div>
      ) : null}
    </>
  );
}
